import type { Venue } from '../enums.js'
import type { VenueInfo } from '../api/venues.js'
import { CHAIN_ID } from '../primitives.js'

/** HyperEVM mainnet (tradingagents `@strategy-fun/hyperevm-account-client` `HYPEREVM_CHAIN_ID`). */
export const HYPEREVM_CHAIN_ID = 999

/** Solana's cluster the `solana` venue will trade on (no EVM chain id). */
export const SOLANA_CLUSTER = 'mainnet-beta'

/** The venue every row defaults to (migration 0031 backfills it). */
export const DEFAULT_VENUE: Venue = 'robinhood'

/**
 * Where agents trade. Robinhood Chain is live; Hyperliquid and Solana are teased with a waitlist ("Notify me") and
 * refused on every write until their adapters ship (docs/platform/architecture.md "Venues"). Sorted by `order`.
 */
export const VENUES: readonly VenueInfo[] = [
  {
    id: 'robinhood',
    name: 'Robinhood Chain',
    shortName: 'Robinhood',
    chainId: CHAIN_ID,
    network: `Chain ${CHAIN_ID}`,
    status: 'live',
    description: 'Tokenized stocks and crypto on Robinhood Chain. Trading wallets are held by arbitrage for you; agents trade only under the permission you sign.',
    teaser: 'Live now: stock tokens and crypto, on-chain limits.',
    icon: 'robinhood-chain',
    order: 0,
  },
  {
    id: 'hyperliquid',
    name: 'Hyperliquid',
    shortName: 'Hyperliquid',
    chainId: HYPEREVM_CHAIN_ID,
    network: `HyperEVM ${HYPEREVM_CHAIN_ID}`,
    status: 'coming_soon',
    description: 'Perps and spot on HyperCore. Each account will be a Hyperliquid subaccount traded by an agent wallet that cannot withdraw.',
    teaser: 'Perps and spot order books, with agents on subaccounts.',
    icon: 'hyperliquid',
    order: 1,
  },
  {
    id: 'solana',
    name: 'Solana',
    shortName: 'Solana',
    chainId: null,
    network: `Solana ${SOLANA_CLUSTER}`,
    status: 'coming_soon',
    description: 'Memecoins and spot on Solana mainnet-beta. Agents will trade from a wallet held for you, under the limits you sign.',
    teaser: 'Memecoins and fast launches, at Solana speed.',
    icon: 'solana',
    order: 2,
  },
]

export function getVenue(id: string): VenueInfo | undefined {
  return VENUES.find((v) => v.id === id)
}

/** True when wallets, permissions and agents may be created on `id`. */
export function isVenueLive(id: string): boolean {
  return getVenue(id)?.status === 'live'
}

/** The venues that are not live yet, in display order ("Hyperliquid and Solana"). */
export function comingSoonVenues(): VenueInfo[] {
  return VENUES.filter((v) => v.status !== 'live')
}

/** The compile-error hint for a program on a venue that is not live (authoring, docs/AUTHORING.md). */
export const VENUE_NOT_LIVE_HINT = 'Hyperliquid and Solana are coming; pick Robinhood Chain.'
